import { create } from "zustand";
import type { CanvasEdge, CanvasNode, SimOverride, SimProfile } from "@opus-dt/shared-types";
import type { MachineDetail } from "@/types/lines.types";
import type { SandboxDraft, SandboxMachine, SandboxPeriod } from "@/types/sim-sandbox.types";
import { DEFAULT_PERIOD } from "@/types/sim-sandbox.types";
import { buildDraftFromLine } from "../sandbox-draft.helpers";
import { createStageActions, edgesFor, nextStageKey } from "./sandbox-stage-actions";
import { mergeMeasuredBases, type MeasuredBaseUpdate } from "./sandbox-measured-bases";

// Edges são DERIVADAS do modelo em camadas (estágios). Recomputadas a cada mutação
// estrutural (present/remove/add/mover-estágio/reordenar) — nunca editadas à mão no canvas novo.
// As ações de estágio/posição vivem em sandbox-stage-actions.ts (mantém este store ≤200 LOC).

// Store do sandbox em MEMÓRIA (nunca escreve na linha real). Espelha o
// padrão de features/lines/draft, mas é um store PRÓPRIO — nunca reusa o da linha.

interface HydrateArgs {
  scenarioId: string | null;
  baseLineId: string;
  baseUpdatedAt: string | null;
  productId: string | null;
  machines: MachineDetail[];
  nodes: CanvasNode[];
  edges: CanvasEdge[];
}

interface AcervoArgs {
  sourceMachineId: string;
  code: string;
  name: string;
  provenance?: "measured" | "assumed";
  /** Estágio de destino. Ausente ⇒ último estágio, ou um novo se não houver nenhum. */
  stageKey?: string;
}

export interface SandboxDraftState extends SandboxDraft {
  hydrate: (args: HydrateArgs) => void;
  hydrateNew: (args: { scenarioId: string; productId: string | null }) => void;
  hydrateFromSnapshot: (draft: SandboxDraft) => void;
  select: (key: string | null) => void;
  togglePresent: (key: string) => void;
  removeMachine: (key: string) => void;
  addAcervoMachine: (args: AcervoArgs) => { ok: boolean; reason?: string };
  setMachineSim: (
    key: string,
    patch: { simProfile?: SimProfile; simOverrides?: SimOverride[] }
  ) => void;
  /** Injeta a base MEDIDA por máquina (do preview do servidor) p/ exibição — NÃO marca dirty nem
   * é persistida. Idempotente: só re-emite quando algum valor muda (evita loop no merge do preview). */
  setMeasuredBases: (bases: Record<string, MeasuredBaseUpdate>) => void;
  /** Move uma máquina para outro estágio (re-deriva edges). */
  setStage: (key: string, stageKey: string) => void;
  /** Cria um estágio ao final; retorna sua key. */
  addStage: (name?: string) => string;
  renameStage: (key: string, name: string) => void;
  /** Remove um estágio; suas máquinas migram p/ o adjacente (bloqueia se for o único). */
  removeStage: (key: string) => void;
  /** Reordena o estágio `key` para o índice `newIndex` (re-normaliza orders + re-deriva edges). */
  reorderStage: (key: string, newIndex: number) => void;
  setProduct: (productId: string) => void;
  setPeriod: (patch: Partial<SandboxPeriod>) => void;
  markSaved: (updatedAt: string, scenarioId?: string) => void;
  reset: () => void;
}

const EMPTY: SandboxDraft = {
  scenarioId: null,
  baseLineId: null,
  baseUpdatedAt: null,
  productId: null,
  machines: {},
  stages: [],
  edges: [],
  start: { key: "__start__", x: 0, y: 0 },
  end: { key: "__end__", x: 600, y: 0 },
  period: { ...DEFAULT_PERIOD },
  selectedKey: null,
  dirty: false,
};

let acervoSeq = 0;

export const useSandboxDraft = create<SandboxDraftState>()((set, get) => ({
  ...EMPTY,

  hydrate: (args) => set({ ...buildDraftFromLine(args) }),

  // Reabrir cenário salvo: rehidrata do structureSnap (edits preservados).
  hydrateFromSnapshot: (draft) => set({ ...draft }),

  // Cenário "linha nova": canvas vazio (só START/END), sem linha base.
  hydrateNew: (args) =>
    set({
      ...EMPTY,
      period: { ...DEFAULT_PERIOD },
      scenarioId: args.scenarioId,
      baseLineId: null,
      productId: args.productId,
    }),

  select: (key) => set({ selectedKey: key }),

  togglePresent: (key) =>
    set((s) => {
      const m = s.machines[key];
      if (!m) return s;
      const machines = { ...s.machines, [key]: { ...m, present: !m.present } };
      return { machines, edges: edgesFor(machines, s.stages), dirty: true };
    }),

  removeMachine: (key) =>
    set((s) => {
      if (!s.machines[key]) return s;
      const machines = { ...s.machines };
      delete machines[key];
      return {
        machines,
        edges: edgesFor(machines, s.stages),
        selectedKey: s.selectedKey === key ? null : s.selectedKey,
        dirty: true,
      };
    }),

  addAcervoMachine: (args) => {
    const s = get();
    const dup = Object.values(s.machines).some(
      (m) => m.present && m.sourceMachineId === args.sourceMachineId
    );
    if (dup) return { ok: false, reason: "Máquina já presente no cenário." };
    // Estágio de destino: explícito, senão o último; se não houver estágio, cria "Estágio 1".
    let stages = s.stages;
    let stageKey = args.stageKey ?? [...stages].sort((a, b) => a.order - b.order).at(-1)?.key;
    if (!stageKey) {
      stageKey = nextStageKey();
      stages = [...stages, { key: stageKey, name: "Estágio 1", order: 0 }];
    }
    const key = `acv-${(acervoSeq += 1)}-${args.sourceMachineId.slice(0, 8)}`;
    const count = Object.keys(s.machines).length;
    const machine: SandboxMachine = {
      key,
      sourceMachineId: args.sourceMachineId,
      origin: "acervo",
      present: true,
      code: args.code,
      name: args.name,
      x: 120 + count * 160,
      y: 140,
      stageKey,
      provenance: args.provenance ?? "assumed",
    };
    const machines = { ...s.machines, [key]: machine };
    set({ machines, stages, edges: edgesFor(machines, stages), dirty: true });
    return { ok: true };
  },

  ...createStageActions(set),

  setMachineSim: (key, patch) =>
    set((s) => {
      const m = s.machines[key];
      if (!m) return s;
      return {
        machines: {
          ...s.machines,
          [key]: {
            ...m,
            ...(patch.simProfile !== undefined && { simProfile: patch.simProfile }),
            ...(patch.simOverrides !== undefined && { simOverrides: patch.simOverrides }),
          },
        },
        dirty: true,
      };
    }),

  setMeasuredBases: (bases) =>
    set((s) => {
      const machines = mergeMeasuredBases(s.machines, bases);
      return machines ? { machines } : s; // sem dirty: base de exibição, não autoria
    }),

  setProduct: (productId) => set({ productId, dirty: true }),

  setPeriod: (patch) => set((s) => ({ period: { ...s.period, ...patch }, dirty: true })),

  markSaved: (updatedAt, scenarioId) =>
    set((s) => ({
      dirty: false,
      baseUpdatedAt: updatedAt,
      scenarioId: scenarioId ?? s.scenarioId,
    })),

  reset: () => set({ ...EMPTY, period: { ...DEFAULT_PERIOD } }),
}));
